import { countries } from '@/data/countries';
import {
  normalizeSetupCountryCode,
  resolveSetupCheckoutLocale,
  resolveSetupCountryFromLocale,
} from '@/components/setup/experience/lib/setupCheckoutCountry';
import { parsePriceString } from '@/components/setup/setupManualSchemas';
import type { SetupShippingRateDraft } from '@/components/setup/experience/lib/setupShippingRateTypes';
import type { ShippingRateItem } from '@/lib/api/shippingRates';

export interface SetupShippingSummary {
  rateCount: number;
  previewRateName: string;
  previewRatePrice: string;
  previewRateCountries: string;
  /** ISO country used for checkout preview shipping rates */
  previewCountryCode?: string;
}

function formatRatePrice(rate: ShippingRateItem, currency: string, freeLabel: string): string {
  if (rate.isFree) return freeLabel;

  const options = rate.amount ?? {};
  const priceCurrency = options[currency] ? currency : Object.keys(options)[0];
  const amountMinor = priceCurrency ? options[priceCurrency]?.amount : undefined;

  if (amountMinor == null) return '—';

  return new Intl.NumberFormat(undefined, { style: 'currency', currency: priceCurrency ?? currency }).format(
    amountMinor / 100,
  );
}

function countryLabelFromCode(code: string): string {
  const alpha2 = normalizeSetupCountryCode(code);
  if (!alpha2) return code;

  const country = countries.find((entry) => entry['alpha-2'] === alpha2);
  return country?.name ?? alpha2;
}

function formatRateCountries(
  rate: ShippingRateItem,
  allCountriesLabel: string,
  formatMultiCountryLabel: (count: number) => string,
): string {
  const codes = rate.countryCodes ?? [];
  if (!codes.length) return allCountriesLabel;
  if (codes.length === 1) return countryLabelFromCode(codes[0]!);
  return formatMultiCountryLabel(codes.length);
}

export function buildSetupShippingSummary(
  rates: ShippingRateItem[] | undefined,
  currency: string,
  freeLabel: string,
  allCountriesLabel: string,
  formatMultiCountryLabel: (count: number) => string,
  i18nLanguage?: string,
): SetupShippingSummary | null {
  if (!rates?.length) return null;

  const previewRate = rates[0];

  const previewCountryCode =
    normalizeSetupCountryCode(previewRate.countryCodes?.[0]) ??
    (previewRate.countryCodes?.length
      ? null
      : resolveSetupCountryFromLocale(resolveSetupCheckoutLocale(i18nLanguage)));

  return {
    rateCount: rates.length,
    previewRateName: previewRate.shippingRateName?.trim() ?? '',
    previewRatePrice: formatRatePrice(previewRate, currency, freeLabel),
    previewRateCountries: formatRateCountries(previewRate, allCountriesLabel, formatMultiCountryLabel),
    ...(previewCountryCode ? { previewCountryCode } : {}),
  };
}

/**
 * Builds the session preview snapshot from the full set of form drafts (post create/update). The
 * preview fields come from the first draft, mirroring {@link buildSetupShippingSummary}.
 */
export function buildSetupShippingSnapshotFromDrafts(
  drafts: SetupShippingRateDraft[],
  currency: string,
  freeLabel: string,
  allCountriesLabel: string,
  formatMultiCountryLabel: (count: number) => string,
  i18nLanguage?: string,
): SetupShippingSummary | null {
  if (!drafts.length) return null;

  const preview = mergeSetupShippingAfterCreate(
    null,
    [drafts[0]],
    currency,
    freeLabel,
    allCountriesLabel,
    formatMultiCountryLabel,
    i18nLanguage,
  );

  return { ...preview, rateCount: drafts.length };
}

export function mergeSetupShippingAfterCreate(
  existing: SetupShippingSummary | null | undefined,
  rates: SetupShippingRateDraft[],
  currency: string,
  freeLabel: string,
  allCountriesLabel: string,
  formatMultiCountryLabel: (count: number) => string,
  i18nLanguage?: string,
): SetupShippingSummary {
  const lastRate = rates[rates.length - 1];
  const priceAmount = lastRate?.priceMajor ? parsePriceString(lastRate.priceMajor) : null;

  const previewCountryCode =
    normalizeSetupCountryCode(lastRate?.countryCodes?.[0]) ??
    (lastRate?.allCountries || !lastRate?.countryCodes.length
      ? resolveSetupCountryFromLocale(resolveSetupCheckoutLocale(i18nLanguage))
      : null);

  return {
    rateCount: (existing?.rateCount ?? 0) + rates.length,
    previewRateName: lastRate?.shippingRateName?.trim() ?? '',
    previewRatePrice: lastRate?.isFree
      ? freeLabel
      : priceAmount !== null && priceAmount > 0
        ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(priceAmount)
        : freeLabel,
    previewRateCountries:
      lastRate?.allCountries || !lastRate?.countryCodes.length
        ? allCountriesLabel
        : lastRate.countryCodes.length === 1
          ? countryLabelFromCode(lastRate.countryCodes[0]!)
          : formatMultiCountryLabel(lastRate.countryCodes.length),
    ...(previewCountryCode ? { previewCountryCode } : {}),
  };
}
